import React from 'react';
import { motion } from 'framer-motion';
import {
  Check,
  CircleDollarSign,
  FileBarChart2,
  Gauge,
  Shield,
  Smartphone,
} from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import { fadeInUp, stagger, scaleIn } from '../lib/animations';

interface Benefit {
  icon: React.ComponentType<LucideProps>;
  title: string;
  description: string;
}

const benefits: Benefit[] = [
  {
    icon: CircleDollarSign,
    title: 'Economia real no combustível',
    description:
      'Compare etanol e gasolina com base no consumo real do seu carro e pague menos no posto todo dia.',
  },
  {
    icon: Gauge,
    title: 'Controle financeiro completo',
    description:
      'Visualize quanto você gastou em combustível no mês, no trimestre e no ano, por veículo.',
  },
  {
    icon: FileBarChart2,
    title: 'Relatórios claros e detalhados',
    description:
      'Histórico completo com totais por período e os postos mais frequentados, na palma da mão.',
  },
  {
    icon: Smartphone,
    title: 'Interface simples e intuitiva',
    description:
      'Pensado para o dia a dia brasileiro: em português, rápido e sem burocracia para registrar.',
  },
  {
    icon: Shield,
    title: 'Dados seguros e privados',
    description:
      'Seus registros são armazenados com segurança e nunca compartilhados com terceiros.',
  },
];

const checklistItems = [
  'Compare etanol × gasolina em segundos',
  'Registre abastecimentos com foto do cupom',
  'Receba alertas de revisão preventiva',
  'Visualize relatórios mensais e anuais',
  'Gerencie múltiplos veículos na mesma conta',
  'Interface 100% em português brasileiro',
];

// Mini card de estatística de destaque
const StatCard: React.FC<{ value: string; label: string }> = ({ value, label }) => (
  <div className="rounded-2xl border border-gray-100 bg-white p-4 text-center shadow-sm">
    <p className="text-2xl font-bold text-amber-500">{value}</p>
    <p className="mt-1 text-xs leading-snug text-gray-500">{label}</p>
  </div>
);

// Seção "Por que usar o Gas2?" com layout assimétrico desktop
const BenefitsSection: React.FC = () => (
  <section id="beneficios" className="bg-white py-20 sm:py-28">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">

        {/* Coluna esquerda: título + checklist */}
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <motion.span
            variants={fadeInUp}
            className="mb-3 inline-block rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-600"
          >
            Por que escolher Gas2?
          </motion.span>

          <motion.h2
            variants={fadeInUp}
            className="mt-3 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
          >
            Economize de verdade,{' '}
            <span className="text-amber-500">todo mês</span>
          </motion.h2>

          <motion.p variants={fadeInUp} className="mt-4 text-base leading-relaxed text-gray-500">
            Usuários do Gas2 economizam em média{' '}
            <strong className="text-gray-900">12% no custo mensal</strong> com combustível ao tomar
            decisões baseadas nos dados reais do próprio veículo.
          </motion.p>

          <motion.ul variants={stagger} className="mt-8 space-y-3">
            {checklistItems.map((item) => (
              <motion.div
                key={item}
                variants={fadeInUp}
                className="flex items-center gap-3 text-sm text-gray-700"
              >
                <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
                  <Check className="h-3 w-3 text-amber-600" strokeWidth={2.5} />
                </span>
                {item}
              </motion.div>
            ))}
          </motion.ul>
        </motion.div>

        {/* Coluna direita: estatísticas + cards de benefício */}
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          {/* Estatísticas em destaque */}
          <motion.div variants={fadeInUp} className="mb-6 grid grid-cols-3 gap-3">
            <StatCard value="12%" label="economia média mensal" />
            <StatCard value="10k+" label="abastecimentos registrados" />
            <StatCard value="4,9★" label="avaliação dos usuários" />
          </motion.div>

          {/* Lista de benefícios */}
          <div className="space-y-3">
            {benefits.map((b) => (
              <motion.div
                key={b.title}
                variants={scaleIn}
                className="flex items-start gap-4 rounded-2xl border border-gray-100 bg-gray-50 p-4
                  transition-all duration-200 hover:border-amber-200 hover:bg-amber-50/40"
              >
                <span className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-amber-100">
                  <b.icon className="h-5 w-5 text-amber-600" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{b.title}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-gray-500">{b.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

export default BenefitsSection;

